import React, { useEffect, useState } from "react";
import { FaChevronRight } from "react-icons/fa";
import { FieldStatus } from "./FieldStatus";
import { openIOSSettings } from "./NotificationsOffPopup";
import {
  IS_NATIVE,
  BiometricError,
  enrollBiometricSignIn,
  getBiometricAvailability,
  isArmedFor,
  markPrompted,
  type BiometricAvailability,
} from "../lib/biometricSignIn";
import type { User } from "../types/User";
import { t } from "../i18n";

// "Face ID sign-in" in Settings (founder, 1.5.1 #5). The 8/26 switch was
// removed because "once they add it, it stays" — so this row exists only
// while it is OFF for this account on this phone: native app, biometrics on
// the device, this install not armed for this account. Turning it on makes
// the row disappear; nothing here can turn it off. If iOS has blocked Face ID
// for the app ("Don't Allow" on Apple's one-time question), the row sends
// them to the app's page in iPhone Settings instead — Apple never asks twice.
//
// It lives inside the Settings sheet, which renders nothing while closed, so
// every open asks the phone again; so does coming back from iPhone Settings.

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type Props = {
  user: Pick<User, "_id" | "name"> | null;
  /** The sheet's Brand Green line icon for this row. */
  icon: React.ReactNode;
};

export default function FaceIdSettingRow({ user, icon }: Props) {
  const userId = user?._id;
  const [bio, setBio] = useState<BiometricAvailability | null>(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!IS_NATIVE || !userId) return;
    let alive = true;
    const check = () => {
      getBiometricAvailability({ fresh: true }).then((b) => {
        if (!alive) return;
        setBio(b);
        setArmed(isArmedFor(userId));
      });
    };
    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId]);

  if (!IS_NATIVE || !user || !bio || armed || !(bio.available || bio.blocked)) return null;
  const { label } = bio;
  // "{label} sign-in" can start with the label, and Android's is lower case.
  const title = capitalize(t("faceIdSettingRow.signIn", { label }));

  if (bio.blocked) {
    return (
      <button type="button" className="profile-setting-row profile-setting-row--button" onClick={() => openIOSSettings()}>
        {icon}
        <span className="profile-setting-text">
          <span className="profile-setting-label">{title}</span>
          <span className="profile-setting-sub">{t("faceIdSettingRow.enableInSettings", { label })}</span>
        </span>
        <span className="profile-setting-trail">
          <span className="profile-setting-value">{t("faceIdSettingRow.open")}</span>
          <FaChevronRight className="profile-menu-chev" aria-hidden />
        </span>
      </button>
    );
  }

  const turnOn = async () => {
    if (!user || busy) return;
    setBusy(true);
    setError("");
    try {
      await enrollBiometricSignIn(user, { verifyFirst: true });
      markPrompted(user._id);
      setArmed(true); // on for good: the row goes away
    } catch (err) {
      const code = err instanceof BiometricError ? err.code : "failed";
      // Ask the phone again before saying anything: "Don't Allow" on Apple's
      // Face ID question lands here too, and then the next step is iPhone
      // Settings (the row turns into that), not "try again".
      const now = await getBiometricAvailability({ fresh: true });
      setBio(now);
      if (code === "cancelled" || !now.available) return;
      setError(code === "network" ? t("faceIdSettingRow.offline") : t("faceIdSettingRow.failed", { label }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="profile-setting-row">
        {icon}
        <div className="profile-setting-text">
          <div className="profile-setting-label">{title}</div>
          <div className="profile-setting-sub">
            {/touch|finger/i.test(label) ? t("faceIdSettingRow.withATouch") : t("faceIdSettingRow.withAGlance")}
          </div>
        </div>
        {/* Slides on while the phone asks; snaps back if they back out. */}
        <button
          type="button"
          role="switch"
          aria-checked={busy}
          aria-busy={busy}
          aria-label={title}
          className={`profile-toggle${busy ? " on" : ""}`}
          onClick={turnOn}
        >
          <span className="profile-toggle-knob" />
        </button>
      </div>
      <FieldStatus status={error ? { state: "error", msg: error } : undefined} />
    </>
  );
}
